import { Box } from '@mui/material'
import styled from '@emotion/styled/macro'
import theme from 'theme'
import { PERFORMANCE_SECTION_HEIGHT } from '../../utils'

export const PerformanceScoreWrapper = styled(Box)<{ isempty: string }>`
  border-radius: ${theme.attributes.borderRadius.quaternary};
  background: ${theme.colors.surfaceContainerBackground};
  padding: ${theme.spacing.medium}px;
  width: 100%;
  height: ${PERFORMANCE_SECTION_HEIGHT}px;
  display: flex;
  flex-direction: column;

  ${({ isempty }) =>
    isempty === 'true' &&
    `
      opacity: 0.38;
      min-width: 497px;
      height: 367px;
  `};

  .title-wrapper {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;

    .title-block {
      display: flex;
      align-items: center;
    }

    .chart-title {
      color: ${theme.colors.menuItemText};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.xLarge};
      font-style: normal;
      font-weight: ${theme.typography.fontWeight.bold};
      line-height: 24px;
      letter-spacing: 0.1px;
      margin-left: ${theme.spacing.small}px;
    }

    .date {
      color: ${theme.colors.cardDateText};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.medium};
      font-style: normal;
      font-weight: ${theme.typography.fontWeight.normal};
      line-height: 16px;
      letter-spacing: 0.5px;

      span {
        color: ${theme.colors.foreground};

        html[data-theme='light'] & {
          font-weight: ${theme.typography.fontWeight.extraThick};
        }
      }
    }
  }

  /* Takes whatever the title row leaves, so a wrapped title shortens the row instead of overflowing the card. */
  .pizza-chart-container {
    padding: ${theme.spacing.large}px 0 0 0;
    display: flex;
    align-items: stretch;
    justify-content: center;
    gap: ${theme.spacing.medium}px;
    flex: 1;
    min-height: 0;
  }

  /* Capped at the 1920px design size, shrinking in step with the list on narrower cards. */
  .pizza-chart-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 1 261px;
    min-width: 0;

    svg {
      max-width: 100%;
      height: auto;
    }
  }
`

export const OverallScoreWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  border-radius: ${theme.attributes.borderRadius.normal};
  background: ${theme.colors.legacyPrimaryLight3};
  box-shadow: ${theme.attributes.boxShadow.overallScoreWrapper};

  html[data-theme='light'] & {
    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.08);
  }
  margin: ${theme.spacing.small}px 0;

  .score-value {
    color: ${theme.colors.cta};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.xxLarge};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 28px;
  }

  .score-label {
    color: ${theme.colors.cta};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.mediumSmall};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 16px;
    letter-spacing: 0.5px;
    margin-left: ${theme.spacing.xSmall}px;
    margin-bottom: 2px;
  }
`

export const EmptyPerformanceCategoriesWrapper = styled(Box)`
  width: 214px;
  height: 195px;
  flex: none;
  align-self: center;
  box-shadow: ${theme.attributes.boxShadow.overallScoreWrapper};

  html[data-theme='light'] & {
    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.08);
  }
  border-radius: ${theme.attributes.borderRadius.quaternary};
  margin-left: ${theme.spacing.lg};

  ::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: 1280px) {
    margin-left: 45px;
  }

  .category-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: ${theme.spacing.small}px ${theme.spacing.md}px;

    &:not(:last-of-type) {
      border-bottom: 1px solid ${theme.colors.strokePrimary};
    }

    &:first-of-type {
      border-radius: ${theme.spacing.small}px ${theme.spacing.small}px 0 0;
    }

    &:last-of-type {
      border-radius: 0 0 ${theme.spacing.small}px ${theme.spacing.small}px;
    }

    &:hover {
      background: ${theme.colors.legacyPrimaryLight3};
      cursor: pointer;
      border-bottom: 1px solid ${theme.colors.controlBorder};
    }

    .name {
      color: ${theme.colors.calendarDay};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.mediumSmall};
      font-style: normal;
      font-weight: ${theme.typography.fontWeight.bold};
      line-height: 16px;
      letter-spacing: 0.5px;
    }

    .value {
      color: ${theme.colors.calendarDay};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.mediumSmall};
      font-style: normal;
      font-weight: ${theme.typography.fontWeight.bold};
      line-height: 16px;
      letter-spacing: 0.5px;
      margin-left: ${theme.spacing.xSmall}px;
    }
  }
`

export const PerformanceCategoriesWrapper = styled(Box)`
  flex: 0 1 250px;
  min-width: 0;
  background: ${theme.colors.legacyBackgroundMedium};
  border: 1px solid ${theme.colors.strokePrimary};
  box-shadow: ${theme.attributes.boxShadow.overallScoreWrapper};

  html[data-theme='light'] & {
    background: #fafafb;
    border-color: ${theme.colors.strokeSecondary};
    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.08);
  }
  border-radius: ${theme.attributes.borderRadius.tertiary};
  overflow-y: auto;
  overflow-x: hidden;

  ::-webkit-scrollbar {
    width: 4px;
    background: transparent;
  }

  ::-webkit-scrollbar-track {
    box-shadow: none;
    background: transparent;
    margin-top: 15px;
    margin-bottom: 15px;
  }

  ::-webkit-scrollbar-thumb {
    background-color: ${theme.colors.legacy292128};
    border-radius: ${theme.attributes.borderRadius.secondary};
  }

  ::-webkit-scrollbar-corner {
    background: transparent;
  }

  .performance-categories-inner {
    width: 100%;
    padding: 0 4px 20px 12px;
  }
`

export const PerformanceCategoryItem = styled(Box)<{ isselected?: string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.xSmall}px;
  padding: ${theme.spacing.small}px ${theme.spacing.md}px;
  border-bottom: 1px solid transparent;

  &:not(:last-of-type) {
    border-bottom: 1px solid ${theme.colors.strokePrimary};
  }

  &:first-of-type {
    border-radius: ${theme.attributes.borderRadius.quaternary} ${theme.attributes.borderRadius.quaternary} 0 0;
  }

  &:last-of-type {
    // border-radius: 0 0 ${theme.attributes.borderRadius.quaternary} ${theme.attributes.borderRadius.quaternary};
  }

  &:hover {
    cursor: pointer;
    border-bottom: 1px solid ${theme.colors.controlBorder};

    .value,
    .name {
      color: ${theme.colors.cta};
      text-decoration-line: underline;
    }
  }

  /* Breaks rather than pushing the row wider than the list once the card narrows. */
  .name {
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.medium};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 16px;
    letter-spacing: 0.5px;
    min-width: 0;
    overflow-wrap: anywhere;
  }

  ${({ isselected }) =>
    isselected &&
    isselected === 'true' &&
    `
    background: ${theme.colors.legacyPrimaryLight3};
    cursor: pointer;
    border-bottom: 1px solid ${theme.colors.controlBorder};
    
    .name {
      color: ${theme.colors.cta};
      text-decoration-line: underline;
    }
  `};

  .value {
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.mediumSmall};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 16px;
    letter-spacing: 0.5px;
    margin-left: ${theme.spacing.xSmall}px;
    flex-shrink: 0;
  }

  .explore-category {
    color: ${theme.colors.cta};
    text-align: center;
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.mediumSmall};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 16px;
    letter-spacing: 0.5px;

    &:hover {
      text-decoration: underline;
    }
  }
`
